/**
 * Consignment status codes and their display names
 */
export const CONSIGNMENT_STATUS = {
  AC: 'AC',
  CA: 'CA',
  CL: 'CL',
  IP: 'IP',
  NR: 'NR',
  OH: 'OH'
} as const;

export type ConsignmentStatusCode = (typeof CONSIGNMENT_STATUS)[keyof typeof CONSIGNMENT_STATUS];

/**
 * Badge variant used to colour-code each consignment status in the
 * consignments list (mirrors the service-requests status badge).
 * Values are a subset of the Badge component's variants.
 */
export type ConsignmentStatusVariant = 'default' | 'success' | 'warning' | 'destructive' | 'secondary';

/**
 * Badge props (variant + optional extra classes) for a consignment status.
 * `className` carries colours the design-system Badge does not ship as
 * variants (WFP orange for On Hold).
 */
export interface ConsignmentStatusBadge {
  variant: ConsignmentStatusVariant;
  className?: string;
}

/**
 * Maps every consignment status code to its Badge props / colour.
 */
export const CONSIGNMENT_STATUS_BADGE: Record<ConsignmentStatusCode, ConsignmentStatusBadge> = {
  [CONSIGNMENT_STATUS.NR]: { variant: 'default' }, // New — info blue
  [CONSIGNMENT_STATUS.AC]: { variant: 'success' }, // Accepted — green
  [CONSIGNMENT_STATUS.IP]: { variant: 'warning' }, // In Progress — amber
  [CONSIGNMENT_STATUS.OH]: { variant: 'secondary', className: 'bg-orange-200 text-orange-800' }, // On Hold — orange
  [CONSIGNMENT_STATUS.CA]: { variant: 'destructive' }, // Cancelled — red
  [CONSIGNMENT_STATUS.CL]: { variant: 'secondary' } // Closed — gray
};

/**
 * Returns the Badge props for a consignment status code. Unknown or
 * empty codes fall back to `secondary` (neutral grey).
 */
export function getConsignmentStatusBadge(statusCode: string): ConsignmentStatusBadge {
  return CONSIGNMENT_STATUS_BADGE[statusCode as ConsignmentStatusCode] ?? { variant: 'secondary' };
}

/**
 * Statuses that allow editing consignment lines
 */
export const EDITABLE_STATUSES = [
  CONSIGNMENT_STATUS.NR,
  CONSIGNMENT_STATUS.AC,
  CONSIGNMENT_STATUS.IP // Only if no first receive
] as const;

/**
 * Arrival type codes
 */
export const ARRIVAL_TYPE = {
  CROSS_DOCK: 'X',
  CUSTOMS_CLEARANCE: 'C',
  PREPOSITIONING: 'P',
  IN_KIND: 'I'
} as const;

/**
 * Service type codes
 */
export const SERVICE_TYPE = {
  DISTRIBUTION: 'D',
  PREPOSITIONING: 'P',
  STORAGE: 'S'
} as const;

/**
 * Attachment type codes
 */
export const ATTACHMENT_TYPE = {
  WAYBILL: 'WB',
  PACKING_LIST: 'PL',
  PROOF_OF_COLLECTION: 'PC',
  GOODS_IN: 'GI',
  GOODS_OUT: 'GO',
  LOSS_REPORT: 'LR',
  OTHER: 'OT'
} as const;

export const ATTACHMENT_TYPE_NAMES: Record<string, string> = {
  [ATTACHMENT_TYPE.WAYBILL]: 'Waybill',
  [ATTACHMENT_TYPE.PACKING_LIST]: 'Packing List',
  [ATTACHMENT_TYPE.PROOF_OF_COLLECTION]: 'Proof of Collection',
  [ATTACHMENT_TYPE.GOODS_IN]: 'Goods In',
  [ATTACHMENT_TYPE.GOODS_OUT]: 'Goods Out',
  [ATTACHMENT_TYPE.LOSS_REPORT]: 'Loss Report',
  [ATTACHMENT_TYPE.OTHER]: 'Other'
};

/**
 * Cancellation reason codes
 */
export const CANCELLATION_REASON = {
  ADMINISTRATIVE: 'A',
  LOST: 'L',
  SENDER_REQUEST: 'S',
  ERROR: 'E',
  OTHER: 'O'
} as const;

/**
 * Density constraints for weight/volume validation
 */
export const DENSITY_LIMITS = {
  MIN: 1.2, // kg/m³ - density of air
  MAX: 7870 // kg/m³ - density of iron
} as const;

/**
 * Checks if a consignment status allows editing
 */
export function isEditableStatus(statusCode: string): boolean {
  return statusCode === CONSIGNMENT_STATUS.NR || statusCode === CONSIGNMENT_STATUS.AC;
}

/**
 * Checks if a consignment line can be edited based on status and first receive
 */
export function canEditConsignmentLine(statusCode: string, hasFirstReceive: boolean): boolean {
  if (statusCode === CONSIGNMENT_STATUS.NR || statusCode === CONSIGNMENT_STATUS.AC) {
    return true;
  }
  if (statusCode === CONSIGNMENT_STATUS.IP && !hasFirstReceive) {
    return true;
  }
  return false;
}

/**
 * Validates density based on weight and volume
 */
export function validateDensity(
  weightKg: number,
  volumeM3: number
): {
  isValid: boolean;
  density: number;
  error?: string;
} {
  if (volumeM3 <= 0) {
    return { isValid: false, density: 0, error: 'Volume must be greater than 0' };
  }

  const density = weightKg / volumeM3;

  if (density < DENSITY_LIMITS.MIN) {
    return {
      isValid: false,
      density,
      error: `Density ${density.toFixed(2)} kg/m³ is less than air density (${DENSITY_LIMITS.MIN} kg/m³)`
    };
  }

  if (density > DENSITY_LIMITS.MAX) {
    return {
      isValid: false,
      density,
      error: `Density ${density.toFixed(2)} kg/m³ exceeds iron density (${DENSITY_LIMITS.MAX} kg/m³)`
    };
  }

  return { isValid: true, density };
}
